/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowRight,
  BarChart3,
  Factory,
  Layers,
  CheckCircle2,
  Droplets,
  Zap,
  Link as LinkIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

// --- Components ---

const OptimizedImage = ({
  src,
  alt,
  className,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & { src: string }) => {
  const webpSrc = src.replace(/\.(png|jpg|jpeg)$/i, '.webp');

  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={webpSrc}
        alt={alt}
        className={className}
        {...props}
      />
    </picture>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* 分屏设计：左侧内容 + 右侧视觉重量 */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>

      {/* 工业背景图 — 右侧可见 */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('/hero background.webp')] bg-cover bg-center"></div>
        {/* 左侧深色渐变让文字可读，右侧露出工业图 */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/40"></div>
      </div>

      {/* 动态光效 */}
      <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-primary/10 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* 左侧：内容区 */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="text-white space-y-8"
        >
          {/* 工业风格标签 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm"
          >
            <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse shadow-lg shadow-primary/50"></div>
            <span className="text-sm font-semibold tracking-wide text-white/90">INDUSTRIAL GRADE SOLUTIONS</span>
          </motion.div>

          {/* 更有冲击力的标题 */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1]">
            Turning Waste into
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-emerald-400 to-primary">
              Verified Value
            </span>
          </h1>

          {/* 增加分量的描述 */}
          <p className="text-lg md:text-xl lg:text-2xl text-slate-300 leading-relaxed max-w-xl">
            Heavy-duty thermal conversion equipment that transforms waste into profit.
            <span className="text-primary font-semibold"> No guesswork — just numbers that work.</span>
          </p>

          {/* 更大的CTA按钮 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 pt-4"
          >
            <Link to="/roi-calculator">
              <Button
                variant="brand"
                size="xl"
                className="text-lg px-12 py-7 shadow-2xl hover:shadow-primary/25 transition-all duration-300"
                aria-label="Get your free ROI estimate for thermal conversion equipment"
              >
                Get Your Free ROI Estimate
                <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
            <Link to="/technology">
              <Button
                variant="brandOutline"
                size="xl"
                className="text-lg px-12 py-7"
              >
                View Technology
              </Button>
            </Link>
          </motion.div>

          {/* 信任指标 - 更有视觉重量 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-white/10"
          >
            <div>
              <div className="text-3xl font-bold text-white">10+</div>
              <div className="text-sm text-slate-400">Manufacturers</div>
              <div className="text-xs text-slate-500">in Our Network</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">Up to 45%</div>
              <div className="text-sm text-slate-400">Fuel Oil Yield</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">24h</div>
              <div className="text-sm text-slate-400">Response</div>
            </div>
          </motion.div>
        </motion.div>

        {/* 右侧：工业图 + 数据卡片叠加 */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative hidden lg:block"
        >
          {/* 工业图 — 圆角裁切 */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <OptimizedImage
              src="/hero background.png"
              alt="Industrial thermal conversion equipment"
              className="w-full h-[500px] object-cover"
            />
            {/* 底部渐变让数据卡片可读 */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent"></div>

            {/* 数据卡片叠在图上 */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="text-xs font-bold text-primary tracking-widest">ESTIMATED MONTHLY REVENUE</div>
                </div>
                <div className="text-4xl font-black text-white mb-4">$127,000</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Fuel Oil</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">$57k</span>
                      <div className="flex-1 bg-white/10 rounded-full h-1.5">
                        <div className="bg-primary h-1.5 rounded-full" style={{width: '45%'}}></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Carbon Black</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">$48k</span>
                      <div className="flex-1 bg-white/10 rounded-full h-1.5">
                        <div className="bg-emerald-500 h-1.5 rounded-full" style={{width: '38%'}}></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-300 mb-1">Steel Wire</div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">$22k</span>
                      <div className="flex-1 bg-white/10 rounded-full h-1.5">
                        <div className="bg-slate-400 h-1.5 rounded-full" style={{width: '12%'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 前景装饰 */}
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-primary/30 rounded-full blur-2xl"></div>
        </motion.div>
      </div>

    </section>
  );
};

const RevenueStreams = () => {
  const streams = [
    {
      title: "Carbon Black ≈ 38% Yield",
      category: "INDUSTRIAL VALUE",
      icon: <Zap className="text-primary" size={20} />,
      description: "Solid byproduct can be upgraded into industrial-grade carbon black for rubber manufacturing or sold raw. Processing systems available for high-value grades.",
      image: "/carbon black.png"
    },
    {
      title: "Fuel Oil ≈ 45% Yield",
      category: "PRIMARY PRODUCT",
      icon: <Droplets className="text-primary" size={20} />,
      description: "Pyrolysis converts waste tyres and plastics into fuel oil that can replace diesel. One 10-tonne tyre batch → ~4.5 tonnes of fuel oil. At $0.60/litre local market price, that's roughly $2,700 per batch.",
      image: "/Fuel oil.png"
    },
    {
      title: "Steel Wire ≈ 12% Yield",
      category: "SCRAP REVENUE",
      icon: <LinkIcon className="text-primary" size={20} />,
      description: "Waste tyres contain steel belting separated during pyrolysis and sold to recyclers. Additional revenue at near-zero processing cost.",
      image: "/Steel-Wire.jpg"
    }
  ];

  return (
    <section className="py-32 bg-white relative overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-50 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* 更有冲击力的标题 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-6">
            <span className="text-sm font-bold text-primary">REVENUE STREAMS</span>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight">
            Three Revenue Streams,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-600">
              One Waste Stream
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Every tonne of waste becomes profit. Here's exactly how.
          </p>
        </motion.div>

        {/* 更有视觉重量的卡片 */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-20">
          {streams.map((stream, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15, duration: 0.8 }}
              whileHover={{ y: -12 }}
              className="group h-full"
            >
              <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 overflow-hidden flex flex-col h-full">
                {/* 悬停时出现的背景 */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* 更大更有分量的图标 */}
                <div className="relative z-10 w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {stream.icon}
                </div>

                {/* 更有冲击力的数字 */}
                <div className="relative z-10 text-4xl font-black text-slate-900 mb-2">
                  {stream.title.split('≈')[0].trim()}
                </div>
                <div className="relative z-10 text-lg font-bold text-primary mb-4">
                  {stream.title.split('≈')[1]?.trim() || ''}
                </div>

                {/* 简化的描述 */}
                <p className="relative z-10 text-slate-600 leading-relaxed mb-6 flex-grow">
                  {stream.description}
                </p>

                {/* 视觉重量：百分比指示器 */}
                <div className="relative z-10 pt-6 border-t border-slate-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-slate-500">Revenue Share</span>
                    <span className="text-sm font-black text-primary">
                      {idx === 0 ? '45%' : idx === 1 ? '38%' : '12%'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: idx === 0 ? '45%' : idx === 1 ? '38%' : '12%' }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5, duration: 1 }}
                      className="h-3 rounded-full bg-gradient-to-r from-primary to-emerald-600"
                    ></motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 更有分量的CTA区域 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-12 md:p-16 overflow-hidden"
        >
          {/* 装饰性背景 */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"></div>

          <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Stop Leaving Money on the Table
              </h3>
              <p className="text-xl text-slate-300 leading-relaxed">
                Your waste is worth more than you think. Get your custom ROI analysis and see exactly what you're missing.
              </p>
            </div>
            <Link to="/roi-calculator">
              <Button
                variant="brand"
                size="xl"
                className="text-base px-12 py-6 shadow-2xl"
              >
                Calculate Your ROI
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Advantage = () => {
  const advantages = [
    {
      title: "Business Case First",
      icon: <BarChart3 className="text-primary" />,
      description: "Before you buy anything, we build your financial model — feedstock yield, revenue projection, payback timeline. You see the numbers before you see the invoice."
    },
    {
      title: "Factory Direct",
      icon: <Factory className="text-primary" />,
      description: "Direct access to 10+ manufacturers in Yucheng, Henan — China's most concentrated thermal conversion equipment cluster. No trading company markup."
    },
    {
      title: "End-to-End",
      icon: <Layers className="text-primary" />,
      description: "Equipment matching, quality verification, shipping coordination, installation liaison. One point of contact from first conversation to first barrel of fuel."
    }
  ];

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">THE VERIGREEN ADVANTAGE</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-12">Why Industry Leaders <br /> Choose VeriGreen</h2>
          
          <div className="space-y-10">
            {advantages.map((adv, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex gap-6"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center ring-2 ring-primary/20">
                  {adv.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{adv.title}</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {adv.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <OptimizedImage src="/why 1.png" alt="Welding" className="rounded-xl w-full aspect-square object-cover shadow-lg ring-1 ring-border/50" referrerPolicy="no-referrer" loading="lazy" fetchPriority="low" />
            <OptimizedImage src="/Why 3.png" alt="Pipes" className="rounded-xl w-full aspect-[3/4] object-cover shadow-lg ring-1 ring-border/50" referrerPolicy="no-referrer" loading="lazy" fetchPriority="low" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4 pt-12"
          >
            <OptimizedImage src="/Why 2.png" alt="Blueprint" className="rounded-xl w-full aspect-[3/4] object-cover shadow-lg ring-1 ring-border/50" referrerPolicy="no-referrer" loading="lazy" fetchPriority="low" />
            <OptimizedImage src="/why 4.png" alt="Plant" className="rounded-xl w-full aspect-square object-cover shadow-lg ring-1 ring-border/50" referrerPolicy="no-referrer" loading="lazy" fetchPriority="low" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ROICalculator = () => {
  const countries = [
    'Thailand', 'India', 'Indonesia', 'Nigeria', 'South Africa',
    'Brazil', 'Mexico', 'Egypt', 'Kenya', 'Philippines',
    'United States', 'Malaysia', 'Vietnam', 'Pakistan', 'Bangladesh',
    'Turkey', 'Saudi Arabia', 'UAE', 'Colombia', 'Ghana'
  ];

  return (
    <section id="roi" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">See What Your Waste Is Worth</h2>
          <p className="text-xl text-slate-600 mb-12">
            Tell us three things about your operation, and we'll send you a custom ROI estimate — free, no strings attached.
          </p>

          <form className="bg-white p-8 md:p-10 rounded-2xl border border-border shadow-lg space-y-6 hover-lift">
            <fieldset>
              <legend className="block text-sm font-semibold text-foreground mb-3">Primary Waste Type</legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['Tyres', 'Plastics', 'Both'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    className="py-3 px-4 rounded-lg border-2 border-input bg-background text-sm font-semibold text-muted-foreground hover:border-primary hover:text-primary hover:bg-accent transition-all focus-ring"
                    aria-label={`Select ${type} as primary waste type`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="monthly-volume" className="block text-sm font-semibold text-foreground mb-2">Monthly Volume (Tonnes)</label>
              <input
                id="monthly-volume"
                type="number"
                placeholder="e.g. 500"
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                aria-describedby="volume-help"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="country" className="block text-sm font-semibold text-foreground mb-2">Operation Country</label>
                <select id="country" className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all appearance-none cursor-pointer">
                  <option>Select country</option>
                  {countries.map((country) => (
                    <option key={country}>{country}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact" className="block text-sm font-semibold text-foreground mb-2">Email or WhatsApp</label>
                <input
                  id="contact"
                  type="text"
                  placeholder="+1..."
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                  aria-label="Email address or WhatsApp number"
                />
              </div>
            </div>

            <Button variant="brand" size="lg" className="w-full text-base shadow-lg">
              Get My Free ROI Estimate <ArrowRight size={20} />
            </Button>

            <p className="text-center text-sm text-slate-500">
              Or chat with us on WhatsApp{' '}
              <a
                href="https://wa.me/85259379574?text=Hi%20VERIGREEN%2C%20I%27m%20interested%20in%20pyrolysis%20equipment"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-semibold hover:underline inline-flex items-center gap-1"
              >
                →
              </a>
            </p>

            <div className="flex items-start gap-3 pt-2">
              <CheckCircle2 className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <p className="text-xs text-slate-500 leading-relaxed">
                We reply within 24 hours with a custom PDF report: estimated yield, revenue projection, equipment recommendation, and payback timeline.
              </p>
            </div>
          </form>
        </div>

        <div className="relative">
          {/* Decorative elements for the report mockup */}
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
          
          <motion.div 
            initial={{ opacity: 0, rotate: 5, y: 50 }}
            whileInView={{ opacity: 1, rotate: 0, y: 0 }}
            viewport={{ once: true }}
            className="relative z-10 bg-slate-900 p-4 rounded-3xl shadow-2xl border-4 border-slate-800 max-w-md mx-auto hover-lift"
          >
            <div className="bg-background rounded-2xl overflow-hidden p-8 min-h-[500px]">
              <div className="flex justify-between items-start mb-12">
                <div>
                  <p className="text-[10px] font-black tracking-widest text-primary uppercase mb-1">VERIGREEN REPORT</p>
                  <div className="w-12 h-1 bg-primary rounded-full"></div>
                </div>
                <p className="text-[10px] font-bold text-slate-400 uppercase">CONFIDENTIAL ANALYSIS</p>
              </div>

              <div className="space-y-8">
                <div>
                  <div className="w-24 h-2 bg-slate-100 rounded-full mb-4"></div>
                  <div className="w-full h-10 bg-slate-900 rounded-xl"></div>
                </div>

                <div>
                  <div className="flex justify-between items-end mb-4">
                    <p className="text-xs font-bold text-slate-900">Feedstock Analysis</p>
                    <p className="text-[10px] font-bold text-primary">Verified</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-12 bg-slate-100 rounded-lg"></div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-900 mb-4">Revenue Projection</p>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: '75%' }}
                      transition={{ duration: 1.5, delay: 0.5 }}
                      className="h-full bg-primary"
                    ></motion.div>
                  </div>
                </div>

                <div className="pt-8 mt-8 border-t border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">Estimated Payback Period</p>
                  <p className="text-4xl font-black text-slate-900">14.2 MONTHS</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- Home Page ---

export function Home() {
  return (
    <main>
      <Hero />
      <RevenueStreams />
      <Advantage />
      <ROICalculator />
    </main>
  );
}

export default Home;
